import * as characterKeys from "src/config/characters"
import clsx from "clsx"
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react"

export type CharacterParams = {
  role: string
  sex: string
  hair_style: string
  hair_arrange: string
  hair_color: string
  eye_color: string
  age: string
  height: string
  weight: string
}

type Props = {
  selected: CharacterParams | undefined
  setSelected: Dispatch<SetStateAction<CharacterParams | undefined>>
}

const SelectCharacters: React.FC<Props> = ({ selected, setSelected }) => {
  // 年齢
  const [age, setAge] = useState(["", ""])

  // 身長
  const [height, setHeight] = useState(["", ""])

  // 体重
  const [weight, setWeight] = useState(["", ""])

  const getSexCls = useCallback((sid: number) => {
    if (sid === 1) {
      return "-male"
    } else if (sid === 2) {
      return "-female"
    } else {
      return "-child"
    }
  }, [])

  const onChangeSelect = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!e.target.value) {
        setSelected(selected && { ...selected, [key]: "" })
      } else {
        setSelected(selected && { ...selected, [key]: e.target.value })
      }
    },
    [selected, setSelected],
  )

  const onChangeSex = useCallback(
    (value: string) => {
      if (value === selected?.sex) {
        setSelected({ ...selected, sex: "" })
      } else {
        setSelected(selected && { ...selected, sex: value })
      }
    },
    [selected, setSelected],
  )

  const onChangeRange = useCallback(
    (key: string) =>
      (isMin: boolean) =>
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        let setter = null
        let params = null
        if (key === "age") {
          setter = setAge
          params = age
        } else if (key === "height") {
          setter = setHeight
          params = height
        } else {
          setter = setWeight
          params = weight
        }
        const tmp = [...params]
        if (isMin) {
          tmp[0] = value
        } else {
          tmp[1] = value
        }
        setter(tmp)

        const str = tmp.join("-")
        if (str === "-") {
          setSelected(selected && { ...selected, [key]: "" })
        } else {
          setSelected(selected && { ...selected, [key]: str })
        }
      },
    [age, height, selected, setSelected, weight],
  )

  useEffect(() => {
    if (selected?.age) {
      setAge(selected.age.split("-"))
    } else {
      setAge(["", ""])
    }
    if (selected?.height) {
      setHeight(selected.height.split("-"))
    } else {
      setHeight(["", ""])
    }
    if (selected?.weight) {
      setWeight(selected.weight.split("-"))
    } else {
      setWeight(["", ""])
    }
  }, [selected?.age, selected?.height, selected?.weight])

  return (
    <div className="p-searchCharacter-list">
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-role">役割</label>
        </div>
        <div className="p-searchCharacter-body">
          <span className="c-select-primary -full">
            <select
              className="c-select-primary-body"
              name="search-role"
              id="search-role"
              onChange={onChangeSelect("role")}
              value={selected?.role}
            >
              <option value="">---</option>
              {characterKeys.roles.map((k) => (
                <option key={k.id} value={`${k.id}`}>
                  {k.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-gender-male">性別</label>
        </div>
        <div className="p-searchCharacter-body">
          <div className="p-searchCharacter-gender">
            {characterKeys.sex.map((k) => (
              <label key={k.id} className="p-searchCharacter-gender-item">
                <input
                  className="p-searchCharacter-gender-input"
                  type="checkbox"
                  value={k.id}
                  // onChange={onChangeSelect("sex")}
                  onChange={(e) => onChangeSex(e.target.value)}
                  checked={k.id === Number(selected?.sex)}
                />
                <span
                  className={clsx(
                    "p-searchCharacter-gender-text",
                    getSexCls(k.id),
                  )}
                >
                  {k.name}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-style">ヘアスタイル</label>
        </div>
        <div className="p-searchCharacter-body">
          <span className="c-select-primary -full">
            <select
              className="c-select-primary-body"
              name="search-style"
              id="search-style"
              onChange={onChangeSelect("hair_style")}
              value={selected?.hair_style}
            >
              <option value="">---</option>
              {characterKeys.hair_style.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-arrangement">ヘアアレンジ</label>
        </div>
        <div className="p-searchCharacter-body">
          <span className="c-select-primary -full">
            <select
              className="c-select-primary-body"
              name="search-arrangement"
              id="search-arrangement"
              onChange={onChangeSelect("hair_arrange")}
              value={selected?.hair_arrange}
            >
              <option value="">---</option>
              {characterKeys.hair_arrange.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-hair">髪の色</label>
        </div>
        <div className="p-searchCharacter-body">
          <span className="c-select-primary -full">
            <select
              className="c-select-primary-body"
              name="search-hair"
              id="search-hair"
              onChange={onChangeSelect("hair_color")}
              value={selected?.hair_color}
            >
              <option value="">---</option>
              {characterKeys.hair_color.map((k) => (
                <option key={k.id} value={`${k.id}`}>
                  {k.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-eye">瞳の色</label>
        </div>
        <div className="p-searchCharacter-body">
          <span className="c-select-primary -full">
            <select
              className="c-select-primary-body"
              name="search-eye"
              id="search-eye"
              onChange={onChangeSelect("eye_color")}
              value={selected?.eye_color}
            >
              <option value="">---</option>
              {characterKeys.eye_color.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-age01">年齢</label>
        </div>
        <div className="p-searchCharacter-body">
          <div className="p-multiSelect">
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                id="search-age01"
                onChange={onChangeRange("age")(true)}
                value={age[0]}
              >
                <option value="">---</option>
                <option value="10">10代</option>
                <option value="20">20代</option>
                <option value="30">30代</option>
                <option value="40">40代</option>
                <option value="50">50代</option>
                <option value="60">60代</option>
              </select>
            </span>
            <span className="p-multiSelect-separate">〜</span>
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                name="search-age02"
                id="search-age02"
                onChange={onChangeRange("age")(false)}
                value={age[1]}
              >
                <option value="">---</option>
                <option value="10">10代</option>
                <option value="20">20代</option>
                <option value="30">30代</option>
                <option value="40">40代</option>
                <option value="50">50代</option>
                <option value="60">60代</option>
              </select>
            </span>
          </div>
        </div>
      </div>
      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-height01">身長</label>
        </div>
        <div className="p-searchCharacter-body">
          <div className="p-multiSelect">
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                name="search-height01"
                id="search-height01"
                onChange={onChangeRange("height")(true)}
                value={height[0]}
              >
                <option value="">---</option>
                <option value="100">100cm</option>
                <option value="120">120cm</option>
                <option value="140">140cm</option>
                <option value="160">160cm</option>
                <option value="180">180cm</option>
                <option value="200">200cm</option>
                <option value="201">201cm以上</option>
              </select>
            </span>
            <span className="p-multiSelect-separate">〜</span>
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                name="search-height02"
                id="search-height02"
                onChange={onChangeRange("height")(false)}
                value={height[1]}
              >
                <option value="">---</option>
                <option value="100">100cm</option>
                <option value="120">120cm</option>
                <option value="140">140cm</option>
                <option value="160">160cm</option>
                <option value="180">180cm</option>
                <option value="200">200cm</option>
                <option value="201">201cm以上</option>
              </select>
            </span>
          </div>
        </div>
      </div>

      <div className="p-searchCharacter-item">
        <div className="p-searchCharacter-label">
          <label htmlFor="search-height01">体重</label>
        </div>
        <div className="p-searchCharacter-body">
          <div className="p-multiSelect">
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                name="search-height01"
                id="search-height01"
                onChange={onChangeRange("weight")(true)}
                value={weight[0]}
              >
                <option value="">---</option>
                <option value="30">30kg</option>
                <option value="40">40kg</option>
                <option value="50">50kg</option>
                <option value="60">60kg</option>
                <option value="70">70kg</option>
                <option value="80">80kg</option>
                <option value="90">90kg</option>
                <option value="100">100kg</option>
              </select>
            </span>
            <span className="p-multiSelect-separate">〜</span>
            <span className="p-multiSelect-select c-select-primary">
              <select
                className="p-multiSelect-select-body c-select-primary-body"
                name="search-height02"
                id="search-height02"
                onChange={onChangeRange("weight")(false)}
                value={weight[1]}
              >
                <option value="">---</option>
                <option value="30">30kg</option>
                <option value="40">40kg</option>
                <option value="50">50kg</option>
                <option value="60">60kg</option>
                <option value="70">70kg</option>
                <option value="80">80kg</option>
                <option value="90">90kg</option>
                <option value="100">100kg</option>
              </select>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectCharacters
